/**
 * Platform audit pages (/woocommerce-cro-audit, /wix-cro-audit ...).
 *
 * These pages exist for search: they are linked from the footer, the sitemap
 * and the related solution pages, never from the menu. Each one has to earn its
 * place with problems and checks that are specific to the platform, not the
 * Shopify page with the name swapped (that would be a doorway page).
 *
 * Rendered by src/pages/seo/PlatformSolution.tsx and, for crawlers, by
 * src/seo/ssrBodies.ts. Title, H1, intro and FAQs live in route-meta.ts.
 *
 * Inline links use a small markdown form, [label](/path), which both renderers
 * understand. Plain TS with no imports, because Deno reads it too.
 */

export type PlatformSlug = "woocommerce" | "bigcommerce" | "magento" | "wix" | "squarespace" | "webflow";

export type PlatformPage = {
  slug: PlatformSlug;
  path: string;
  /** Platform name as written in copy. */
  name: string;
  eyebrow: string;
  /** Which demo page the hero shows: the KYMA store or the HALDEN lead page. */
  visual: "store" | "lead";
  /** Which sample report the page opens. */
  demo: "cro" | "leadgen";
  demoTitle: string;
  demoBody: string;
  /** Prose before the checklist. */
  intro: { h2: string; body: string[] }[];
  checklistTitle: string;
  checklistLead: string;
  checks: { area: string; items: string[] }[];
  /** Prose after the checklist. */
  outro: { h2: string; body: string[] }[];
  howTitle: string;
  steps: string[];
  closing: string;
};

const MEASURES = (name: string, subject: string) =>
  `Two layers. The first is measured in code and comes out the same every time you run it: which elements rank highest in visual weight on the page, whether ${subject} wins that ranking, how many elements compete with it in the decision zone, whether the contrast passes legibility thresholds, what is visible without scrolling and what is not, and how the page on a phone differs from the page on a laptop. The second layer is interpreted: what each measured fact means for a ${name} site like yours, which change is worth making first, and what the page would look like fixed. A finding that cannot point at a measured element is dropped before you see it.`;

const STORE_DEMO_BODY =
  "On the KYMA demo store the report finds an Add to bag button that reads as disabled, a rating 1,260 px below the price and delivery facts folded into an accordion, each with the numbers and the fix.";

const LEAD_DEMO_BODY =
  "On the Northstar demo page the report reads the whole page: it credits the outcome-led first screen, then finds the price and the strongest testimonial far from the form, nine fields where five would do, a pale \"Submit\" and no promise of what happens next, each with the fix.";

export const PLATFORMS: Record<PlatformSlug, PlatformPage> = {
  woocommerce: {
    slug: "woocommerce",
    path: "/woocommerce-cro-audit",
    name: "WooCommerce",
    eyebrow: "WooCommerce CRO",
    visual: "store",
    demo: "cro",
    demoTitle: "See what the audit finds on a WooCommerce-style product page",
    demoBody: STORE_DEMO_BODY,
    intro: [
      {
        h2: "Why WooCommerce stores lose sales on pages that look finished",
        body: [
          "WooCommerce runs on WordPress, so a product page is assembled from layers that were never designed together: the theme's template, a page builder such as Elementor or Divi, and plugins for reviews, swatches, upsells, wishlists, popups and payments. Each layer adds markup, scripts and one more thing competing for the shopper's eye. The page looks finished in the editor, but on a phone the Add to cart button can sit below the gallery, a reviews carousel and two promotional banners.",
          "The checkout has its own history. Stores set up before WooCommerce 8.3 usually run the classic shortcode checkout, newer stores get the Cart and Checkout blocks, and many run a mix, with plugins that support only one of them. The result is fields, notices and payment buttons in places the shopper does not expect. None of this shows up as an error in the dashboard. Visitors simply leave.",
        ],
      },
      {
        h2: "What the audit measures on a WooCommerce page",
        body: [MEASURES("WooCommerce", "your Add to cart button")],
      },
    ],
    checklistTitle: "The WooCommerce CRO checklist we audit against",
    checklistLead:
      "Use it by hand if you like. The audit checks the same list on your own pages, then tells you which items fail and in what order to fix them.",
    checks: [
      {
        area: "Product page",
        items: [
          "Price, stock status and Add to cart on the first screen on mobile, not pushed down by the gallery or a page builder section",
          "Variations chosen with visible swatches or buttons rather than dropdowns, with unavailable combinations shown before the click",
          "Add to cart wins the visual weight ranking; wishlist, compare and share icons stay quiet",
          "Delivery cost and time stated on the product page, not left to the shipping calculator in the cart",
          "Star rating and review count near the price, not only inside the Reviews tab",
          "Returns summarised in one line near the button",
          "Related products and upsells below the buy box, never between the price and the button",
        ],
      },
      {
        area: "Shop and category pages",
        items: [
          "Filters for the attributes shoppers actually use (size, price, colour), with counts, that work on a phone",
          "Product cards with one image, the price and a clear sale price, without a wall of badges",
          "Default sorting that puts popular or relevant products first, not the newest upload",
        ],
      },
      {
        area: "Cart",
        items: [
          "One primary Proceed to checkout button, visible without scrolling on mobile",
          "A shipping estimate before checkout, and progress towards free shipping if you offer it",
          "Coupon field available but collapsed, so it does not send shoppers off to hunt for codes",
          "Cross-sells limited to a few relevant products, placed below the checkout button",
        ],
      },
      {
        area: "Checkout",
        items: [
          "Guest checkout switched on (Allow customers to place orders without an account)",
          "Express payments such as Apple Pay, Google Pay and PayPal at the top, from your payment plugin",
          "Only the fields you need: company, second address line and order notes optional or removed",
          "Error messages next to the field that failed, not only in a notice at the top of the page",
          "Secure payment, returns and a way to contact you visible near Place order",
        ],
      },
      {
        area: "Speed and plugins",
        items: [
          "Largest Contentful Paint under 2.5 seconds on mobile for the product page",
          "No layout shift from review widgets, cookie banners or popups that load late (CLS under 0.1)",
          "Plugin scripts loaded only on the pages that use them",
          "Popups delayed until the shopper has seen the product, and never covering the button on a phone",
        ],
      },
    ],
    outro: [
      {
        h2: "Plugins, page builders and the problems they leave behind",
        body: [
          "Most WooCommerce conversion problems arrive one plugin at a time. A swatch plugin that loads its script on every page. A reviews widget that pushes the layout down as it loads, just as the shopper reaches for the button. A mini cart that refreshes itself on every page view. An exit popup that fires on the second visit as well as the first. Page builders make it easy to add a section above the buy box and hard to notice that the button has moved below the fold on a 390 px screen.",
          "The audit sees the page the way a shopper does, with every plugin rendered, so it catches what the WordPress dashboard never shows you. For the speed side, pair it with the [technical audit](/solutions/technical-analysis), which measures Core Web Vitals and names the scripts that slow the page down.",
        ],
      },
    ],
    howTitle: "How to run a WooCommerce CRO audit",
    steps: [
      "Paste the URL of your best-selling product page. It carries the most revenue, so start there.",
      "Confirm the business model (e-commerce) and the page type the scan detected.",
      "Read the verdict first, then the attention heatmap for desktop and mobile, then the fixes in order.",
      "Make the top three changes in your theme, builder or plugin settings, then run the audit again to check that the fix landed.",
      "Repeat on the cart and the checkout. For those pages the audit adds a product to the cart before it captures them.",
    ],
    closing:
      "See [the plans](/pricing) to run it on your own store, work through the [WooCommerce checkout optimization guide](/woocommerce-checkout-optimization) for the checkout fixes, read the [e-commerce CRO guide](/ecommerce-cro-guide) for the strategy behind each fix, learn [how a CRO audit works](/cro-audit) step by step, or put your store next to a competitor's with [competitor comparison](/solutions/competitor-comparison).",
  },

  bigcommerce: {
    slug: "bigcommerce",
    path: "/bigcommerce-cro-audit",
    name: "BigCommerce",
    eyebrow: "BigCommerce CRO",
    visual: "store",
    demo: "cro",
    demoTitle: "See what the audit finds on a product page",
    demoBody: STORE_DEMO_BODY,
    intro: [
      {
        h2: "Where BigCommerce stores lose the sale",
        body: [
          "BigCommerce is often the platform a store moves to when it has outgrown a simpler one: thousands of SKUs, several customer groups, wholesale and retail on the same site. The checkout is fast and well tested, so the leaks are usually upstream. Shoppers cannot find the right product among 400 in a category, cannot tell which option is in stock, or cannot see what they will pay once a customer group price or shipping is applied.",
          "Stencil themes such as Cornerstone give every store a solid start, but they are built to show every feature at once: quick view, compare, wishlist, share, reviews, related products. On a product page with long names, many options and a promotions banner, Add to cart competes with a dozen other elements before the shopper has decided anything.",
        ],
      },
      {
        h2: "What the audit measures on a BigCommerce page",
        body: [MEASURES("BigCommerce", "Add to cart")],
      },
    ],
    checklistTitle: "The BigCommerce CRO checklist we audit against",
    checklistLead:
      "Category pages come first on purpose: on a large catalogue most visits land there, not on a product.",
    checks: [
      {
        area: "Category and search",
        items: [
          "Faceted filters for the attributes buyers use, with result counts, that work on mobile without a full reload",
          "Product cards that show price, stock and the main option, not only a product name",
          "Search that handles SKUs, part numbers and misspellings, and suggests categories when nothing matches",
          "Sorting and pagination that keep the shopper's place when they come back from a product",
        ],
      },
      {
        area: "Product page",
        items: [
          "Price, stock and Add to cart on the first screen on mobile",
          "Options such as size, colour or pack size as buttons or swatches, with unavailable ones disabled before the click",
          "Bulk pricing or quantity breaks shown next to the quantity field",
          "Delivery estimate and returns in one line near the button",
          "Reviews with an average and a count near the price",
          "Quick view, compare and wishlist visually quieter than Add to cart",
        ],
      },
      {
        area: "Cart and checkout",
        items: [
          "One primary checkout button, with the shipping estimate and total visible",
          "Guest checkout on, with account creation offered after the order",
          "Express payment buttons visible at the top of checkout",
          "Promotions and the coupon field placed so they do not push the checkout button down",
        ],
      },
      {
        area: "B2B and trade buyers",
        items: [
          "A clear path to prices for signed-out trade buyers: sign in, apply for an account or request a quote",
          "Quote and quick-order entry points visible where buyers look for them",
          "Minimum order quantities and lead times stated before the cart",
        ],
      },
      {
        area: "Speed and apps",
        items: [
          "Largest Contentful Paint under 2.5 seconds on mobile for category and product pages",
          "Scripts from apps and tracking tags loaded after the page is usable",
          "No layout shift from banners, review widgets or chat that load late (CLS under 0.1)",
        ],
      },
    ],
    outro: [
      {
        h2: "Big catalogues need category pages that decide for the shopper",
        body: [
          "On a large catalogue the category page does most of the selling. The audit treats it as a decision page: can a shopper narrow 400 products to 10 in two taps, does each card give enough to choose without opening it, and does the phone show products on the first screen or a wall of filters and banners before the first one. Findings on category pages usually move more revenue than any change to a single product page.",
          "To see where shoppers drop between the category, the product and the cart, run a [funnel audit](/solutions/funnel-analysis) across the three pages.",
        ],
      },
    ],
    howTitle: "How to run a BigCommerce CRO audit",
    steps: [
      "Paste the URL of your busiest category page, then your best-selling product page.",
      "Confirm the business model (e-commerce) and the page type the scan detected.",
      "Read the verdict, the attention heatmap for desktop and mobile, and the fixes in order.",
      "Make the top changes in the theme editor or Page Builder and run the audit again to confirm them.",
      "Finish with the cart and checkout. The audit adds a product to the cart before it captures them.",
    ],
    closing:
      "See [the plans](/pricing), read the [e-commerce CRO guide](/ecommerce-cro-guide) and [how to run a CRO audit](/cro-audit), or benchmark a category page against a competitor with [competitor comparison](/solutions/competitor-comparison).",
  },

  magento: {
    slug: "magento",
    path: "/magento-cro-audit",
    name: "Magento",
    eyebrow: "Magento CRO",
    visual: "store",
    demo: "cro",
    demoTitle: "See what the audit finds on a product page",
    demoBody: STORE_DEMO_BODY,
    intro: [
      {
        h2: "Why Magento stores convert below what their traffic deserves",
        body: [
          "Magento stores usually have strong products and serious traffic, and their conversion problems come from the platform's weight and flexibility. The default Luma theme ships a lot of JavaScript, so on a mid-range phone the product page can take seconds before the options respond to a tap. Configurable products with many attributes turn the buy box into a form. Layered navigation lists every attribute in the catalogue, whether or not it helps anyone choose.",
          "The default checkout has two steps, shipping and then review and payment, and few stores leave it as it came. Extensions add delivery dates, gift messages, company and VAT fields, loyalty points and extra payment methods. Each one made sense on its own. Together they ask the shopper for more than they expect, in an order they did not expect.",
        ],
      },
      {
        h2: "What the audit measures on a Magento page",
        body: [MEASURES("Magento", "Add to Cart")],
      },
    ],
    checklistTitle: "The Magento CRO checklist we audit against",
    checklistLead: "It applies to Magento Open Source and Adobe Commerce alike; the B2B section only to stores that sell to trade buyers.",
    checks: [
      {
        area: "Category pages and layered navigation",
        items: [
          "Filters limited to the attributes shoppers use to choose, with counts, and the most used ones open by default",
          "Filtering on mobile that does not reset the scroll position or reload the whole page",
          "Product cards with price, stock and a clear special price",
          "Search that finds SKUs and synonyms, and suggests categories when nothing matches",
        ],
      },
      {
        area: "Product page",
        items: [
          "Price, stock and Add to Cart on the first screen on mobile",
          "Configurable options as swatches or buttons, with out-of-stock combinations shown before the click",
          "Tier prices and quantity discounts next to the quantity field",
          "Delivery estimate and returns near the button, not inside a tab",
          "Review summary near the price",
          "Upsells, related products and cross-sells below the buy box",
        ],
      },
      {
        area: "Cart and checkout",
        items: [
          "Mini cart that confirms what was added and offers one clear checkout button",
          "Guest checkout enabled, with account creation after the order",
          "Shipping methods and costs visible before payment details are requested",
          "Only the fields you need; company, VAT number and order comments optional where you can",
          "Payment methods, security and returns visible next to Place Order",
        ],
      },
      {
        area: "B2B on Adobe Commerce",
        items: [
          "Request a quote, quick order and requisition lists visible to the buyers who use them",
          "Company account sign-up explained in one line for new trade buyers",
          "Minimum quantities and lead times shown on the product page",
        ],
      },
      {
        area: "Performance",
        items: [
          "Largest Contentful Paint under 2.5 seconds on mobile, with the main product image not lazy loaded",
          "Interaction to Next Paint under 200 ms when a shopper picks an option",
          "No layout shift from sliders, banners or review widgets that load late (CLS under 0.1)",
        ],
      },
    ],
    outro: [
      {
        h2: "Luma, Hyvä and headless: why the frontend matters for conversion",
        body: [
          "Many Magento stores are moving from Luma to a lighter frontend such as Hyvä, or to a headless PWA, to win speed back. A faster theme fixes load time, not the layout decisions carried over from the old one. Because the audit judges the rendered page, it works on all of them, which makes it a useful check around a migration: run it on the current product page, run it again on the new one, and compare what changed in attention, friction and trust.",
          "For load time, render blocking and the scripts behind a slow page, add the [technical audit](/solutions/technical-analysis).",
        ],
      },
    ],
    howTitle: "How to run a Magento CRO audit",
    steps: [
      "Paste the URL of your best-selling configurable product, or of the category that brings the most traffic.",
      "Confirm the business model (e-commerce) and the page type the scan detected.",
      "Read the verdict, the attention heatmap for desktop and mobile, and the fixes in priority order.",
      "Hand the top changes to your developer or agency with the before and after from the report, then run the audit again once they ship.",
      "Audit the cart and checkout next. The audit adds a product to the cart before it captures them.",
    ],
    closing:
      "See [the plans](/pricing), read the [e-commerce CRO guide](/ecommerce-cro-guide) and [how to run a CRO audit](/cro-audit), or compare your product page with a competitor's using [competitor comparison](/solutions/competitor-comparison).",
  },

  wix: {
    slug: "wix",
    path: "/wix-cro-audit",
    name: "Wix",
    eyebrow: "Wix CRO",
    visual: "store",
    demo: "cro",
    demoTitle: "See what the audit finds on a store product page",
    demoBody: STORE_DEMO_BODY,
    intro: [
      {
        h2: "Why Wix sites look finished and still do not convert",
        body: [
          "Wix templates are designed to look complete from the first minute: a full-screen image, a large headline, a row of icons. That works for a portfolio. For a store or a service business it often means the offer, the price and the button sit below a hero image that says nothing about what you sell, and on a phone that hero fills the whole first screen.",
          "The Wix Editor also keeps a separate mobile layout. Elements can be hidden on mobile, stacked in a different order, or left overlapping after a change on desktop, and nobody notices because the desktop page is the one that gets checked. EyeCaptain captures both versions and reports what a mobile visitor sees, and what they never see.",
        ],
      },
      {
        h2: "What the audit measures on a Wix page",
        body: [MEASURES("Wix", "your main button")],
      },
    ],
    checklistTitle: "The Wix CRO checklist we audit against",
    checklistLead: "The first screen and mobile sections apply to every Wix site. The other two depend on what the page is for.",
    checks: [
      {
        area: "First screen, on every page",
        items: [
          "A headline that says what you sell and to whom, not only the business name",
          "One primary button on the first screen on mobile, with a verb that says what happens next",
          "A hero image that shows the product or the result, not stock scenery",
        ],
      },
      {
        area: "Wix Stores product page",
        items: [
          "Price, options and Add to Cart visible without scrolling on a phone",
          "Shipping cost and delivery time stated on the product page",
          "Reviews near the price, from Wix Stores reviews or your review app",
          "Returns and secure payment in one line near the button",
          "Related products below the buy box, not above it",
        ],
      },
      {
        area: "Service, booking and contact pages",
        items: [
          "A price or a price range, and what is included",
          "Availability or the next free slot shown before asking for personal details",
          "Forms with only the fields you need, and a button that says what the visitor gets",
          "Proof next to the form: reviews, photos of real work, the name of the person who will reply",
        ],
      },
      {
        area: "Mobile layout",
        items: [
          "Nothing important hidden on mobile that is visible on desktop",
          "Text large enough to read and buttons at least 44 px tall, with no overlapping elements",
          "A sticky header or chat button that does not cover the main button",
        ],
      },
      {
        area: "Speed and apps",
        items: [
          "Largest Contentful Paint under 2.5 seconds on mobile; background videos and sliders replaced by a still image on the first screen",
          "Apps and popups from the App Market kept to the ones that earn their place",
        ],
      },
    ],
    outro: [
      {
        h2: "A Wix store and a Wix service site are judged by different rules",
        body: [
          "You pick the business model when you start the audit. A Wix Stores page is judged as e-commerce: the buy box, shipping, reviews and the path to checkout. A page for a salon, a studio or a consultant is judged as lead generation or booking: whether the offer is clear, whether proof sits next to the form or the booking button, and how much a visitor has to type before they get an answer.",
          "The rules behind each are on the [e-commerce CRO audit](/solutions/ecommerce), [lead generation CRO audit](/solutions/lead-generation) and [booking site CRO](/solutions/booking) pages.",
        ],
      },
    ],
    howTitle: "How to run a Wix CRO audit",
    steps: [
      "Paste the URL of the page that matters most: your best product, or the service page your ads point to.",
      "Pick the business model (e-commerce, lead generation or booking) and confirm the page type.",
      "Read the verdict and the attention heatmap for desktop and mobile. On Wix, look first at what the mobile capture is missing.",
      "Make the top three changes in the Wix Editor or Wix Studio, on both layouts, then run the audit again.",
    ],
    closing:
      "See [the plans](/pricing), start with the [free CRO audit](/free-audit), or read [how to run a CRO audit](/cro-audit) and the [CRO checklist](/cro-checklist) for the points every page should pass.",
  },

  squarespace: {
    slug: "squarespace",
    path: "/squarespace-cro-audit",
    name: "Squarespace",
    eyebrow: "Squarespace CRO",
    visual: "lead",
    demo: "leadgen",
    demoTitle: "See what the audit finds on a service page with a form",
    demoBody: LEAD_DEMO_BODY,
    intro: [
      {
        h2: "Why good-looking Squarespace sites lose visitors",
        body: [
          "Squarespace templates are built around large images, generous white space and quiet typography. Those choices make a brand look expensive, and they also make buttons small, pale and easy to miss. A common pattern: a full-width gallery, a headline in a light weight, and the only button styled as an outline in the same colour as the text. The visitor admires the page and leaves without knowing what to do next.",
          "Fluid Engine gives you precise control over the desktop layout and a separate arrangement on mobile. Blocks can end up in a different order on the phone, the button can drop below three images, and a form can land under a long About section. Most Squarespace traffic arrives on phones from social links and search, so the mobile page is the one that decides.",
        ],
      },
      {
        h2: "What the audit measures on a Squarespace page",
        body: [MEASURES("Squarespace", "the button you want pressed")],
      },
    ],
    checklistTitle: "The Squarespace CRO checklist we audit against",
    checklistLead: "Commerce pages and service pages share the first screen and mobile rules, then part ways.",
    checks: [
      {
        area: "First screen",
        items: [
          "A headline that says what you offer and to whom, not a tagline",
          "One primary button on the first screen on mobile, filled rather than outlined, with a clear verb",
          "A hero image that shows the product, the work or the result",
        ],
      },
      {
        area: "Commerce product pages",
        items: [
          "Price, variants and Add to Cart above the fold on mobile",
          "Shipping cost and delivery time on the product page",
          "Reviews or testimonials near the price",
          "Product images that show scale and detail, with zoom that works on touch",
        ],
      },
      {
        area: "Services, portfolio and booking",
        items: [
          "What you do, for whom, and roughly what it costs, before the gallery",
          "Proof near the enquiry point: client names, reviews, results",
          "Form blocks with only the fields you need and a button that says what happens next",
          "Scheduling placed where the decision is made, showing availability before personal details",
        ],
      },
      {
        area: "Mobile and layout",
        items: [
          "The same order of content on mobile as on desktop, or a better one",
          "Buttons at least 44 px tall and text you can read without zooming",
          "An announcement bar and promotional pop-up that never cover the main button",
        ],
      },
      {
        area: "Speed",
        items: [
          "Largest Contentful Paint under 2.5 seconds on mobile, with hero images exported at the size they are shown",
          "Galleries and video backgrounds that do not delay the first screen",
        ],
      },
    ],
    outro: [
      {
        h2: "Design or conversion is a false choice",
        body: [
          "The changes the audit suggests rarely make a Squarespace site look worse. Most are about hierarchy: making the one button that matters the most visible thing in its section, moving a testimonial next to the form instead of the footer, putting the price where the decision is made. The report shows each change on your own page with a before and after, so you can judge the look before you open the editor.",
          "If the words are the problem rather than the layout, the [content audit](/solutions/content-analysis) reads your copy and rewrites the headlines and calls to action.",
        ],
      },
    ],
    howTitle: "How to run a Squarespace CRO audit",
    steps: [
      "Paste the URL of your most visited page: the home page, a service page or your best product.",
      "Pick the business model (lead generation, booking or e-commerce) and confirm the page type.",
      "Read the verdict and the attention heatmap for desktop and mobile, then the fixes in order.",
      "Make the top changes in the editor, on both the desktop and mobile layouts, and run the audit again.",
    ],
    closing:
      "See [the plans](/pricing), start with the [free CRO audit](/free-audit), or read the [lead generation CRO audit](/solutions/lead-generation) page for the rules behind service pages and [how to run a CRO audit](/cro-audit) for the method.",
  },

  webflow: {
    slug: "webflow",
    path: "/webflow-cro-audit",
    name: "Webflow",
    eyebrow: "Webflow CRO",
    visual: "lead",
    demo: "leadgen",
    demoTitle: "See what the audit finds on a lead generation page",
    demoBody: LEAD_DEMO_BODY,
    intro: [
      {
        h2: "Why Webflow sites win design awards and lose leads",
        body: [
          "Webflow is the tool of choice for teams that care about design, and it shows: custom layouts, smooth interactions, bold type. The same freedom produces the most common Webflow conversion problems. Headlines written for the brand rather than the buyer. Hero sections that animate in on load, so the message and the button appear a second after everything else. Three calls to action on the first screen: book a demo, start free, watch the video.",
          "Interactions deserve a special mention. Content that starts invisible and fades in on scroll looks elegant, but it delays the moment a visitor can read the page, can push back Largest Contentful Paint, and on a slower phone leaves empty space where the offer should be. The audit captures the page the way a visitor meets it, on desktop and on mobile.",
        ],
      },
      {
        h2: "What the audit reads on a Webflow page",
        body: [
          "The whole page, not only the form. The headline and what it promises, the offer and what makes it different, the proof and where it sits, every call to action and what competes with it, the form and what it asks for, and how all of it holds up on a phone. " +
            MEASURES("Webflow", "your primary call to action"),
        ],
      },
    ],
    checklistTitle: "The Webflow CRO checklist we audit against",
    checklistLead: "Written for the pages Webflow is mostly used for: SaaS home and pricing pages, agency and service pages, and campaign landing pages.",
    checks: [
      {
        area: "Hero and message",
        items: [
          "A headline that says what the product or service does and for whom, in the buyer's words",
          "A subheadline with the outcome and the one proof point that matters most",
          "One primary call to action on the first screen, with any secondary one visibly quieter",
          "A hero visual that shows the product or the result, not an abstract animation",
        ],
      },
      {
        area: "Offer and proof",
        items: [
          "Customer logos your buyers recognise, with a line on what you did for them",
          "Testimonials with names, roles and companies, placed near the calls to action",
          "Pricing, or a starting price, on the page when buyers expect to see it",
          "Objections answered before the final call to action: setup time, contract length, security",
        ],
      },
      {
        area: "Forms and demo requests",
        items: [
          "Demo and contact forms with only the fields you need for the first conversation",
          "A button that says what happens next, such as Book my demo, not Submit",
          "What happens after the form: reply time, who will be in touch, what to prepare",
          "Calendar booking offered right after the form, if you use one",
        ],
      },
      {
        area: "Interactions and speed",
        items: [
          "Headline, button and hero visible without waiting for an animation",
          "Largest Contentful Paint under 2.5 seconds on mobile and no layout shift from late embeds (CLS under 0.1)",
          "Lottie files, videos and third-party embeds loaded after the first screen",
        ],
      },
      {
        area: "Mobile",
        items: [
          "Navigation and the primary call to action reachable on a phone without scrolling",
          "Tap targets at least 44 px, and nothing that only works on hover",
          "Pricing tables and comparison grids readable on a narrow screen",
        ],
      },
    ],
    outro: [
      {
        h2: "Landing pages for paid campaigns",
        body: [
          "Many Webflow pages are built for one campaign. Pick the lead generation business model and the landing page type, and the audit checks whether the first screen keeps the promise that brought the visitor, whether there is one clear action, and whether the navigation and other exits pull people away from it.",
          "For SaaS pricing and sign-up pages see the [SaaS CRO audit](/solutions/saas); for the whole lead generation playbook see the [lead generation CRO audit](/solutions/lead-generation).",
        ],
      },
    ],
    howTitle: "How to run a Webflow CRO audit",
    steps: [
      "Paste the URL of the page your campaigns or your sales team send people to.",
      "Pick the business model (lead generation, SaaS or e-commerce) and confirm the page type.",
      "Read the verdict and the attention heatmap for desktop and mobile, then the fixes in order.",
      "Make the top three changes in the Designer, publish, and run the audit again on the live page.",
    ],
    closing:
      "See [the plans](/pricing), go through the [Webflow landing page checklist](/webflow-landing-page-checklist) before your next launch, start with the [free CRO audit](/free-audit), read [how to run a CRO audit](/cro-audit), or compare your page with a competitor's using [competitor comparison](/solutions/competitor-comparison).",
  },
};

export const PLATFORM_SLUGS = Object.keys(PLATFORMS) as PlatformSlug[];

export function platformByPath(path: string): PlatformPage | null {
  return Object.values(PLATFORMS).find((p) => p.path === path) ?? null;
}

/** Split "text [label](/path) text" into plain and link parts. */
export function inlineParts(text: string): Array<{ text: string; to?: string }> {
  const out: Array<{ text: string; to?: string }> = [];
  const re = /\[([^\]]+)\]\((\/[^)\s]*)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ text: m[1], to: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}
